/**
 * Shared-preamble detection and split/join for grounding `instruction`
 * fields (redesign spec §4). Pure functions — no React, easy to unit test.
 */

const longestCommonPrefix = (values: string[]): string => {
  if (values.length === 0) return '';
  let prefix = values[0]!;
  values.slice(1).forEach((value) => {
    let i = 0;
    while (i < prefix.length && i < value.length && prefix[i] === value[i]) i += 1;
    prefix = prefix.slice(0, i);
  });
  return prefix;
};

/**
 * The prompt-injection preamble every Instruction seeds with, trimmed back
 * to the nearest sentence boundary ("<text>. ") so a split never lands
 * mid-sentence. Recomputed per load from whatever the base version's seeded
 * `instruction` values actually are — never hardcoded to a known string,
 * since a future base version can change or drop it (open question #2 in
 * the design spec, confirmed: recompute per load).
 */
export const detectSharedPreamble = (instructions: string[]): string => {
  if (instructions.length < 2) return '';
  const prefix = longestCommonPrefix(instructions);
  const boundary = prefix.lastIndexOf('. ');
  return boundary === -1 ? '' : prefix.slice(0, boundary + 2);
};

export interface InstructionSplit {
  preamble: string;
  remainder: string;
}

/**
 * Splits `instruction` into the locked preamble + editable remainder when it
 * starts with the detected `preamble`. Falls back to the whole instruction as
 * an editable remainder (no split, `preamble: ''`) when it doesn't — the
 * mandatory safety valve from §4 for an intent that never had the
 * boilerplate, or a preamble that no longer matches.
 */
export const splitInstruction = (instruction: string, preamble: string): InstructionSplit => {
  if (preamble !== '' && instruction.startsWith(preamble)) {
    return { preamble, remainder: instruction.slice(preamble.length) };
  }
  return { preamble: '', remainder: instruction };
};

/** Inverse of splitInstruction — round-trips by construction. */
export const joinInstruction = ({ preamble, remainder }: InstructionSplit): string =>
  preamble + remainder;
